import { useEffect, useState } from 'react';
import { auditApi, type AuditQueryRow, type IntegrityCheckResult } from '@/api/audit';
import './AuditPage.css';

const PAGE_SIZE = 50;

const STATUS_CLASS: Record<string, string> = {
  success:        'success',
  error:          'denied',
  denied:         'denied',
  rejected:       'denied',
  timeout:        'warn',
  killed:         'warn',
  pendingapproval:'warn',
};

const STATUS_LABEL: Record<string, string> = {
  success:        'OK',
  error:          'ERRO',
  denied:         'NEGADO',
  rejected:       'REJEITADO',
  timeout:        'TIMEOUT',
  killed:         'CANCELADO',
  pendingapproval:'AGUARD.',
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
  return (
    d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
    ' ' +
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  );
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}
function weekAgoStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().split('T')[0];
}

export default function AuditPage() {
  const [entries, setEntries]   = useState<AuditQueryRow[]>([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [integrity, setIntegrity]         = useState<IntegrityCheckResult | null>(null);
  const [checkingIntegrity, setCheckingIntegrity] = useState(false);

  const [search, setSearch]               = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statementType, setStatementType] = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [fromDate, setFromDate]           = useState(weekAgoStr);
  const [toDate, setToDate]               = useState(todayStr);

  // Debounce search + reset page
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch audit entries
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    auditApi.listQueries({
      from:          fromDate ? fromDate + 'T00:00:00Z' : undefined,
      to:            toDate   ? toDate   + 'T23:59:59Z' : undefined,
      search:        debouncedSearch || undefined,
      statementType: statementType   || undefined,
      status:        statusFilter    || undefined,
      page,
      pageSize:      PAGE_SIZE,
    })
      .then(data => {
        if (!cancelled) {
          setEntries(data.items);
          setTotal(data.total);
        }
      })
      .catch(err => {
        if (!cancelled)
          setError(err?.response?.data?.error ?? err?.message ?? 'Erro ao carregar auditoria');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [fromDate, toDate, debouncedSearch, statementType, statusFilter, page]);

  const handleIntegrityCheck = async () => {
    setCheckingIntegrity(true);
    try {
      setIntegrity(await auditApi.checkIntegrity());
    } catch {
      setIntegrity(null);
    } finally {
      setCheckingIntegrity(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="audit-page">
      <div className="audit-header">
        <div>
          <div className="audit-title">Trilha de Auditoria</div>
          <div className="audit-sub">Registro imutável de todas as queries executadas</div>
        </div>
        <div className="audit-actions">
          <button className="btn">↓ Exportar CSV</button>
          <button
            className="btn"
            onClick={handleIntegrityCheck}
            disabled={checkingIntegrity}
          >
            🔒 {checkingIntegrity ? 'Verificando...' : 'Verificar integridade'}
          </button>
        </div>
      </div>

      <div className="audit-filters">
        <input
          className="audit-filter-input"
          placeholder="Filtrar por usuário, conexão ou query..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <input
          type="date"
          className="audit-filter-select"
          value={fromDate}
          onChange={e => { setFromDate(e.target.value); setPage(1); }}
        />
        <input
          type="date"
          className="audit-filter-select"
          value={toDate}
          onChange={e => { setToDate(e.target.value); setPage(1); }}
        />
        <select
          className="audit-filter-select"
          value={statementType}
          onChange={e => { setStatementType(e.target.value); setPage(1); }}
        >
          <option value="">Todos os tipos</option>
          <option value="SELECT">SELECT</option>
          <option value="INSERT">INSERT</option>
          <option value="UPDATE">UPDATE</option>
          <option value="DELETE">DELETE</option>
          <option value="MERGE">MERGE</option>
          <option value="DDL">DDL</option>
        </select>
        <select
          className="audit-filter-select"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">Todos os status</option>
          <option value="success">OK</option>
          <option value="denied">NEGADO</option>
          <option value="error">ERRO</option>
          <option value="timeout">TIMEOUT</option>
          <option value="killed">CANCELADO</option>
        </select>
      </div>

      <div className="audit-table-wrap">
        <table className="audit-table">
          <thead>
            <tr>
              <th>Horário</th>
              <th>Usuário</th>
              <th>Conexão</th>
              <th>Tipo</th>
              <th>Linhas</th>
              <th>Status</th>
              <th>Política aplicada</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Carregando...
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={7} style={{ padding: '12px', color: 'var(--prod)' }}>
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && entries.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Nenhum registro encontrado para os filtros aplicados
                </td>
              </tr>
            )}
            {!loading && !error && entries.map(e => (
              <tr
                key={e.queryId}
                className={`audit-row ${e.status === 'denied' || e.status === 'rejected' ? 'denied' : ''}`}
              >
                <td className="mono">{formatTime(e.startedAt)}</td>
                <td>{e.userLogin}</td>
                <td className="mono">{e.connLabel}</td>
                <td>
                  <span className={`stmt-badge ${(e.statementType ?? 'unknown').toLowerCase()}`}>
                    {e.statementType ?? '?'}
                  </span>
                </td>
                <td className="mono right">
                  {e.rowsReturned ?? e.rowsAffected ?? 0}
                </td>
                <td>
                  <span className={`status-badge ${STATUS_CLASS[e.status] ?? 'warn'}`}>
                    {STATUS_LABEL[e.status] ?? e.status.toUpperCase()}
                  </span>
                </td>
                <td className="mono muted">{e.matchedRules ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="audit-footer">
        <span>
          {total > 0
            ? `${total} registro${total !== 1 ? 's' : ''} · página ${page} de ${totalPages}`
            : 'Nenhum registro'}
          {page > 1 && (
            <button className="btn" style={{ marginLeft: 8, padding: '2px 8px' }} onClick={() => setPage(p => p - 1)}>
              ‹
            </button>
          )}
          {page < totalPages && (
            <button className="btn" style={{ marginLeft: 4, padding: '2px 8px' }} onClick={() => setPage(p => p + 1)}>
              ›
            </button>
          )}
        </span>
        <span className="audit-integrity">
          {integrity ? (
            <>
              <span style={{ color: integrity.broken === 0 ? 'var(--success)' : 'var(--prod)' }}>●</span>
              {integrity.broken === 0
                ? `${integrity.checked} registros verificados — íntegro`
                : `${integrity.broken} violação(ões) detectada(s)!`}
            </>
          ) : (
            <>
              <span style={{ color: 'var(--text-muted)' }}>●</span>
              Integridade não verificada
            </>
          )}
        </span>
      </div>
    </div>
  );
}
